// SPDX-License-Identifier: GPL-3.0-or-later
// Carl, the tank SigmaBot: Context Window bank (THEME.md section 5.4).
// A worried bean bot stands on his tank. The tank fills as the TOKENS targets go down.
// The red beacon over his head shows when all three are down.

import { BoxGeometry, CylinderGeometry, DoubleSide, SphereGeometry, TorusGeometry } from "three";
import { clamp, numberArg } from "../animation.ts";
import { BRAND_HEX } from "../art/canvas.ts";
import { drawTextPanel } from "../art/labels.ts";
import { addBot } from "./bot.ts";
import {
  type Built,
  type CharacterEnv,
  disposeTree,
  makeLabel,
  motion,
  namedGroup,
  outlined,
  put,
  sec,
  solid,
} from "./common.ts";
import type { SigmaBotSpec } from "./sigmabot/index.ts";

/** The look of Carl. */
export const CARL_SPEC: SigmaBotSpec = {
  body: "bean",
  palette: "ocean",
  eyes: "sad",
  mouth: "oh",
  hat: "none",
  accessory: "none",
};

/** Size of one body unit of Carl. He stands on the tank, so he is small. */
const SCALE = 0.55;
/** Height of the tank. The bot stands on its top. */
const TANK_HEIGHT = 90;

const SEGMENT_Y = [14, 42, 70] as const;

export function buildCarl(env: CharacterEnv): Built {
  const group = namedGroup("Carl");

  group.add(
    put(
      new CylinderGeometry(30, 30, 90, 24, 1, true),
      solid(BRAND_HEX.edge, { opacity: 0.3, side: DoubleSide }),
      0,
      45,
      0,
    ),
  );
  for (const y of [1.5, 88.5]) {
    const ring = put(new TorusGeometry(30, 1.5, 8, 32), solid(BRAND_HEX.edge), 0, y, 0);
    ring.rotation.x = Math.PI / 2;
    group.add(ring);
  }

  const segments = SEGMENT_Y.map((y, i) => {
    const seg = outlined(put(new BoxGeometry(36, 26, 36), solid(BRAND_HEX.grayCCC), 0, y, 0));
    seg.name = `CarlFill${i}`;
    seg.visible = false;
    group.add(seg);
    return seg;
  });

  addBot(group, "CarlBot", CARL_SPEC, SCALE).position.y = TANK_HEIGHT;

  // The beacon: a red fill over his head. It shows when the bank is complete.
  const cap = outlined(put(new SphereGeometry(8, 16, 12), solid(BRAND_HEX.mark), 0, 146, 0));
  cap.name = "CarlCap";
  cap.visible = false;
  group.add(cap);

  const label = makeLabel(env, 50, 12, 256, 64, (ctx, w, h) =>
    drawTextPanel(ctx, w, h, ["TOKENS"]),
  );
  label.mesh.position.set(0, 30, 31);
  group.add(label.mesh);

  let filled = 0;
  return {
    group,
    tick(timeMs) {
      const a = motion(env);
      segments.forEach((seg, i) => {
        seg.visible = i < filled;
        seg.position.y = (SEGMENT_Y[i] ?? 0) + 1 * Math.sin(sec(timeMs) * 0.8) * a;
      });
      cap.visible = filled >= 3;
    },
    play(name, arg) {
      if (name === "fill") filled = Math.round(clamp(numberArg(arg, 0), 0, 3));
    },
    dispose() {
      disposeTree(group);
    },
  };
}
